<template>
  <div class="image-editor" v-if="visible">
    <div class="editor-container">
      <!-- 图片显示区域 -->
      <div class="image-container">
        <div class="image-wrapper" ref="imageWrapper">
          <img
            :src="imageUrl"
            :style="imageStyle"
            ref="imageElement"
            @load="onImageLoad"
            alt="编辑图片"
          />
          <!-- 裁剪框 -->
          <div
            class="crop-frame"
            :style="cropFrameStyle"
            @mousedown="startDrag($event)"
            @touchstart="startDrag($event)"
          >
            <!-- 裁剪框的四个角 -->
            <div
              class="crop-corner top-left"
              @mousedown.stop="startResize('top-left', $event)"
              @touchstart.stop="startResize('top-left', $event)"
            ></div>
            <div
              class="crop-corner top-right"
              @mousedown.stop="startResize('top-right', $event)"
              @touchstart.stop="startResize('top-right', $event)"
            ></div>
            <div
              class="crop-corner bottom-left"
              @mousedown.stop="startResize('bottom-left', $event)"
              @touchstart.stop="startResize('bottom-left', $event)"
            ></div>
            <div
              class="crop-corner bottom-right"
              @mousedown.stop="startResize('bottom-right', $event)"
              @touchstart.stop="startResize('bottom-right', $event)"
            ></div>
          </div>
        </div>
      </div>

      <!-- 控制按钮区域 -->
      <div class="control-panel">
        <div class="control-btn-group">
          <div class="control-btn" @click="rotateImage">
            <img src="@/assets/icons/icon-rotation.svg" alt="旋转" />
          </div>
          <div class="bottom-buttons">
            <div class="cancel-btn" @click="handleCancel">取消</div>
            <div class="confirm-btn" @click="handleConfirm">确定</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 隐藏的Canvas用于图片处理 -->
    <canvas ref="canvas" style="display: none"></canvas>
  </div>
</template>

<script>
  export default {
    name: 'ImageEditor',
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      imageUrl: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        imageLoaded: false,
        imageWidth: 0,
        imageHeight: 0,
        rotation: 0,
        showCropFrame: false,
        cropFrame: {
          x: 0,
          y: 0,
          width: 0,
          height: 0
        },
        containerSize: {
          width: 0,
          height: 0
        },
        isDragging: false,
        isResizing: false,
        dragStart: { x: 0, y: 0 },
        resizeStart: { x: 0, y: 0, width: 0, height: 0 },
        resizeCorner: null,
        originalImage: null
      }
    },
    computed: {
      imageStyle() {
        return {
          transform: `rotate(${this.rotation}deg)`,
          transition: 'transform 0.3s ease',
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }
      },
      cropFrameStyle() {
        return {
          left: `${this.cropFrame.x}px`,
          top: `${this.cropFrame.y}px`,
          width: `${this.cropFrame.width}px`,
          height: `${this.cropFrame.height}px`
        }
      }
    },
    watch: {
      visible(newVal) {
        if (newVal) {
          this.initEditor()
        }
      }
    },
    mounted() {
      this.addGlobalListeners()
    },
    beforeDestroy() {
      this.removeGlobalListeners()
    },
    methods: {
      initEditor() {
        this.rotation = 0
        this.showCropFrame = true // 默认显示裁剪框
        this.imageLoaded = false
        this.isDragging = false
        this.isResizing = false
        this.originalImage = null
        this.$nextTick(() => {
          this.updateContainerSize()
        })
      },

      onImageLoad() {
        this.imageLoaded = true
        this.originalImage = this.$refs.imageElement
        this.imageWidth = this.originalImage.naturalWidth
        this.imageHeight = this.originalImage.naturalHeight
        this.updateContainerSize()
        this.initCropFrame()
      },

      updateContainerSize() {
        if (this.$refs.imageWrapper) {
          const rect = this.$refs.imageWrapper.getBoundingClientRect()
          this.containerSize = {
            width: rect.width,
            height: rect.height
          }
        }
      },

      initCropFrame() {
        if (!this.imageLoaded) return

        const containerWidth = this.containerSize.width
        const containerHeight = this.containerSize.height

        const targetRatio = 100 / 130 
        const maxWidth = containerWidth * 0.6
        const maxHeight = containerHeight * 0.6
        
        let cropWidth, cropHeight
        
        if (maxWidth / maxHeight > targetRatio) {
          // 高度限制
          cropHeight = maxHeight
          cropWidth = cropHeight * targetRatio
        } else {
          // 宽度限制
          cropWidth = maxWidth
          cropHeight = cropWidth / targetRatio
        }
        
        const x = (containerWidth - cropWidth) / 2
        const y = (containerHeight - cropHeight) / 2

        this.cropFrame = {
          x,
          y,
          width: cropWidth,
          height: cropHeight
        }
      },

      rotateImage() {
        this.rotation = (this.rotation + 90) % 360
      },

      startDrag(event) {
        event.preventDefault()
        event.stopPropagation()
        this.isDragging = true
        const point = this.getEventPoint(event)
        this.dragStart = {
          x: point.x - this.cropFrame.x,
          y: point.y - this.cropFrame.y
        }
      },

      startResize(corner, event) {
        event.preventDefault()
        event.stopPropagation()
        this.isResizing = true
        this.resizeCorner = corner
        const point = this.getEventPoint(event)
        this.resizeStart = {
          x: this.cropFrame.x,
          y: this.cropFrame.y,
          width: this.cropFrame.width,
          height: this.cropFrame.height,
          mouseX: point.x,
          mouseY: point.y
        }
      },

      getEventPoint(event) {
        const touch = event.touches ? event.touches[0] : event
        const rect = this.$refs.imageWrapper.getBoundingClientRect()
        const point = {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top
        }
        return point
      },

      onMouseMove(event) {
        if (this.isDragging) {
          this.handleDrag(event)
        } else if (this.isResizing) {
          this.handleResize(event)
        }
      },

      onMouseUp() {
        this.isDragging = false
        this.isResizing = false
        this.resizeCorner = null
      },

      handleDrag(event) {
        const point = this.getEventPoint(event)
        const newX = point.x - this.dragStart.x
        const newY = point.y - this.dragStart.y

        // 限制裁剪框在容器内
        const maxX = this.containerSize.width - this.cropFrame.width
        const maxY = this.containerSize.height - this.cropFrame.height

        this.cropFrame.x = Math.max(0, Math.min(newX, maxX))
        this.cropFrame.y = Math.max(0, Math.min(newY, maxY))
      },

      handleResize(event) {
        const point = this.getEventPoint(event)
        const deltaX = point.x - this.resizeStart.mouseX
        const deltaY = point.y - this.resizeStart.mouseY

        let newWidth = this.resizeStart.width
        let newHeight = this.resizeStart.height
        let newX = this.resizeStart.x
        let newY = this.resizeStart.y

        switch (this.resizeCorner) {
          case 'top-left':
            newWidth = this.resizeStart.width - deltaX
            newHeight = this.resizeStart.height - deltaY
            newX = this.resizeStart.x + deltaX
            newY = this.resizeStart.y + deltaY
            break
          case 'top-right':
            newWidth = this.resizeStart.width + deltaX
            newHeight = this.resizeStart.height - deltaY
            newY = this.resizeStart.y + deltaY
            break
          case 'bottom-left':
            newWidth = this.resizeStart.width - deltaX
            newHeight = this.resizeStart.height + deltaY
            newX = this.resizeStart.x + deltaX
            break
          case 'bottom-right':
            newWidth = this.resizeStart.width + deltaX
            newHeight = this.resizeStart.height + deltaY
            break
        }

        // 限制最小尺寸和最大尺寸
        const minSize = 50
        const maxWidth = this.containerSize.width
        const maxHeight = this.containerSize.height

        if (
          newWidth >= minSize &&
          newHeight >= minSize &&
          newWidth <= maxWidth &&
          newHeight <= maxHeight &&
          newX >= 0 &&
          newY >= 0 &&
          newX + newWidth <= maxWidth &&
          newY + newHeight <= maxHeight
        ) {
          this.cropFrame.width = newWidth
          this.cropFrame.height = newHeight
          this.cropFrame.x = newX
          this.cropFrame.y = newY
        }
      },

      addGlobalListeners() {
        document.addEventListener('mousemove', this.onMouseMove)
        document.addEventListener('mouseup', this.onMouseUp)
        document.addEventListener('touchmove', this.onMouseMove)
        document.addEventListener('touchend', this.onMouseUp)
      },

      removeGlobalListeners() {
        document.removeEventListener('mousemove', this.onMouseMove)
        document.removeEventListener('mouseup', this.onMouseUp)
        document.removeEventListener('touchmove', this.onMouseMove)
        document.removeEventListener('touchend', this.onMouseUp)
      },

      // 真正的图片处理函数
      async processImage() {
        if (!this.originalImage) {
          throw new Error('图片未加载')
        }

        const canvas = this.$refs.canvas
        const ctx = canvas.getContext('2d')

        // 计算图片在容器中的实际显示尺寸
        const containerAspect =
          this.containerSize.width / this.containerSize.height
        const imageAspect = this.imageWidth / this.imageHeight

        let displayWidth, displayHeight
        if (imageAspect > containerAspect) {
          displayWidth = this.containerSize.width
          displayHeight = this.containerSize.width / imageAspect
        } else {
          displayHeight = this.containerSize.height
          displayWidth = this.containerSize.height * imageAspect
        }

        // 计算图片在容器中的偏移
        const offsetX = (this.containerSize.width - displayWidth) / 2
        const offsetY = (this.containerSize.height - displayHeight) / 2

        // 设置canvas尺寸
        canvas.width = this.imageWidth
        canvas.height = this.imageHeight

        // 清空canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        // 应用旋转
        if (this.rotation !== 0) {
          ctx.save()
          ctx.translate(canvas.width / 2, canvas.height / 2)
          ctx.rotate((this.rotation * Math.PI) / 180)
          ctx.drawImage(
            this.originalImage,
            -displayWidth / 2,
            -displayHeight / 2,
            displayWidth,
            displayHeight
          )
          ctx.restore()
        } else {
          ctx.drawImage(this.originalImage, 0, 0, canvas.width, canvas.height)
        }

        // 应用裁剪
        if (this.cropFrame) {
          const scaleX = this.imageWidth / displayWidth
          const scaleY = this.imageHeight / displayHeight

          // 计算裁剪区域在原图中的位置
          const cropX = (this.cropFrame.x - offsetX) * scaleX
          const cropY = (this.cropFrame.y - offsetY) * scaleY
          const cropWidth = this.cropFrame.width * scaleX
          const cropHeight = this.cropFrame.height * scaleY

          // 创建新的canvas来存储裁剪后的图片
          const cropCanvas = document.createElement('canvas')
          const cropCtx = cropCanvas.getContext('2d')

          cropCanvas.width = cropWidth
          cropCanvas.height = cropHeight

          // 绘制裁剪区域
          cropCtx.drawImage(
            canvas,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight
          )

          return new Promise(resolve => {
            cropCanvas.toBlob(
              blob => {
                const reader = new FileReader()
                reader.onload = () => {
                  resolve(reader.result)
                }
                reader.readAsDataURL(blob)
              },
              'image/jpeg',
              0.9
            )
          })
        }

        // 如果没有裁剪，直接返回处理后的图片
        return new Promise(resolve => {
          canvas.toBlob(
            blob => {
              const reader = new FileReader()
              reader.onload = () => {
                resolve(reader.result)
              }
              reader.readAsDataURL(blob)
            },
            'image/jpeg',
            0.9
          )
        })
      },

      handleCancel() {
        this.$emit('cancel')
      },

      async handleConfirm() {
        try {
          this.$toast.loading({
            message: '处理中...',
            forbidClick: true
          })

          const processedImageUrl = await this.processImage()

          const result = {
            url: processedImageUrl,
            rotation: this.rotation,
            crop: this.cropFrame,
            originalUrl: this.imageUrl
          }

          this.$toast.clear()
          this.$emit('confirm', result)
        } catch (error) {
          this.$toast.clear()
          this.$toast('图片处理失败')
        }
      }
    }
  }
</script>

<style lang="less" scoped>
  .image-editor {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #000;
    z-index: 2000;
    display: flex;
    flex-direction: column;
  }

  .editor-container {
    flex: 1;
    height: 0;
    display: flex;
    flex-direction: column;
  }

  .image-container {
    flex: 1;
    height: 0;
    position: relative;
    overflow: hidden;
    background-color: #000;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #000;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  }

  .crop-frame {
    position: absolute;
    border: none;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
    cursor: move;
    z-index: 15;

    .crop-corner {
      position: absolute;
      width: 30px;
      height: 30px;
      background-repeat: no-repeat;
      background-size: contain;
      background-position: center;
      z-index: 20;
      pointer-events: auto;

      &.top-left {
        top: -3px;
        left: -5px;
        cursor: nw-resize;
        background-image: url('~@/assets/icons/crop-corner-top-left.svg');
      }

      &.top-right {
        top: -3px;
        right: -5px;
        cursor: ne-resize;
        background-image: url('~@/assets/icons/crop-corner-top-right.svg');
      }

      &.bottom-left {
        bottom: -7px;
        left: -5px;
        cursor: sw-resize;
        background-image: url('~@/assets/icons/crop-corner-bottom-left.svg');
      }

      &.bottom-right {
        bottom: -7px;
        right: -5px;
        cursor: se-resize;
        background-image: url('~@/assets/icons/crop-corner-bottom-right.svg');
      }
    }
  }

  .control-panel {
    background-color: #000;
    padding: 50/20rem 20/20rem 20/20rem;
    height: 182/20rem;
    flex-shrink: 0;

    .control-btn-group {
      display: flex;
      align-items: center;
    }
  }

  .control-btn {
    color: #fff;
    font-size: 14px;
    padding-right: 20/20rem;
    border-right: 1px solid rgba(255, 255, 255, 0.3);
    height: 24/20rem;

    img {
      width: 24/20rem;
      height: 24/20rem;
    }
  }

  .bottom-buttons {
    flex: 1;
    width: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .cancel-btn {
    color: #fff;
    font-size: 16/20rem;
    width: 74/20rem;
    height: 46/20rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .confirm-btn {
    color: #fff;
    font-size: 16/20rem;
    width: 160/20rem;
    height: 46/20rem;
    border: 1px solid #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
